/**
 * Страница согласования воронок для SMM-специалистов.
 * 
 * URL: /approval/{token}
 * Доступна без авторизации (публичный endpoint).
 */
import { useState, useEffect, useCallback } from 'react';
import { getApprovalData, submitApprovalDecisions, getScheduleStatus } from '../api/approval';
import { VkMediaSlider } from './VkMediaSlider';

function getTokenFromUrl() {
  const pathParts = window.location.pathname.split('/');
  const approvalIndex = pathParts.indexOf('approval');
  if (approvalIndex !== -1 && pathParts[approvalIndex + 1]) {
    return pathParts[approvalIndex + 1];
  }
  return null;
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatDateTime(dateStr) {
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PostCard({ post, decision, onApprove, onReject, onCommentChange, channelName, channelAvatarUrl, brandTimezone, onPreviewClick }) {
  const [showComment, setShowComment] = useState(decision?.decision === 'rejected' || !!decision?.comment);
  const [showFullText, setShowFullText] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);

  const renderText = () => {
    if (post.text_segments && post.text_segments.length > 0) {
      return (
        <div className="text-gray-200 whitespace-pre-wrap text-sm leading-relaxed">
          {post.text_segments.map((segment, idx) => {
            if (segment.kind === 'text') {
              return <span key={idx}>{segment.value}</span>;
            }
            if (segment.kind === 'button') {
              return (
                <a
                  key={idx}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onPreviewClick?.(segment.button_type, segment.product_uid, post.id);
                  }}
                  className="text-[#4DA3FF] hover:text-[#7DBDFF] cursor-pointer no-underline transition-colors font-medium"
                  title={`Тип: ${segment.button_type}${segment.product_uid ? `, товар: ${segment.product_uid}` : ''}`}
                >
                  {segment.label}
                </a>
              );
            }
            return null;
          })}
        </div>
      );
    }
    return <p className="text-gray-200 whitespace-pre-wrap text-sm">{post.text}</p>;
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
      <div className="flex items-start gap-4 mb-4">
        {channelAvatarUrl && (
          <img
            src={channelAvatarUrl}
            alt={channelName}
            className="w-12 h-12 rounded-full object-cover border-2 border-white/20"
          />
        )}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-white font-semibold">Пост #{post.order}</h3>
            {post.scheduled_at && (
              <span className="text-blue-400 text-sm">
                {formatDateTime(post.scheduled_at)}
              </span>
            )}
          </div>
          <div className="bg-white/5 rounded-lg p-4 mb-4">
            {post.media_files && post.media_files.length > 0 && (
              <div className="mb-4">
                <VkMediaSlider
                  mediaFiles={post.media_files}
                  onMediaClick={(index) => setSelectedMediaIndex(index)}
                />
              </div>
            )}
            {renderText()}
          </div>
        </div>
      </div>

      <div className="flex gap-3 mb-3">
        <button
          onClick={onApprove}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            decision?.decision === 'approved'
              ? 'bg-green-600 text-white'
              : 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
          }`}
        >
          ✓ Одобрить
        </button>
        <button
          onClick={onReject}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            decision?.decision === 'rejected'
              ? 'bg-red-600 text-white'
              : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
          }`}
        >
          ✕ Отклонить
        </button>
      </div>

      {decision?.decision === 'rejected' && (
        <div className="mt-3">
          <label className="text-gray-400 text-sm mb-1 block">Комментарий (обязательно при отклонении):</label>
          <textarea
            value={decision?.comment || ''}
            onChange={(e) => onCommentChange(e.target.value)}
            className="w-full bg-white/5 border border-white/20 rounded-lg p-3 text-white text-sm resize-none"
            rows={3}
            placeholder="Укажите причину отклонения..."
          />
        </div>
      )}

      {decision?.decision === 'approved' && (
        <div className="mt-3">
          <label className="text-gray-400 text-sm mb-1 block">Комментарий (необязательно):</label>
          <textarea
            value={decision?.comment || ''}
            onChange={(e) => onCommentChange(e.target.value)}
            className="w-full bg-white/5 border border-white/20 rounded-lg p-3 text-white text-sm resize-none"
            rows={2}
            placeholder="Комментарий к одобрению..."
          />
        </div>
      )}

      {post.previous_decisions && post.previous_decisions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <h4 className="text-gray-400 text-sm font-medium mb-2">Предыдущие решения:</h4>
          {post.previous_decisions.map((prev, idx) => (
            <div key={idx} className="bg-white/5 rounded-lg p-3 mb-2 text-sm">
              <div className="flex items-center justify-between mb-1">
                <span className="text-gray-300">{prev.created_by_name}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  prev.decision === 'approved' ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                }`}>
                  {prev.decision === 'approved' ? 'Одобрен' : 'Отклонён'}
                </span>
              </div>
              {prev.comment && <p className="text-gray-400">{prev.comment}</p>}
              <p className="text-gray-500 text-xs mt-1">Раунд #{prev.round_number}</p>
            </div>
          ))}
        </div>
      )}

      {/* Media Lightbox */}
      {selectedMediaIndex !== null && post.media_files && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setSelectedMediaIndex(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full mx-4">
            <button
              onClick={() => setSelectedMediaIndex(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {post.media_files[selectedMediaIndex]?.type === 'video' ? (
              <div className="bg-gray-800 rounded-lg aspect-video flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500 flex items-center justify-center">
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <p>Видео</p>
                </div>
              </div>
            ) : (
              <img
                src={post.media_files[selectedMediaIndex]?.url}
                alt=""
                className="max-w-full max-h-[85vh] object-contain mx-auto rounded-lg"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ApprovalPage() {
  const token = getTokenFromUrl();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [scheduleStatus, setScheduleStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [decisions, setDecisions] = useState(new Map());
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const openPreview = (buttonType, productUid, postId) => {
    const hashParts = [`approval_token=${encodeURIComponent(token)}`];
    if (postId) {
      hashParts.push(`post_id=${postId}`);
    }
    if (buttonType) {
      hashParts.push(`button_type=${encodeURIComponent(buttonType)}`);
    }
    if (productUid) {
      hashParts.push(`product_uid=${encodeURIComponent(productUid)}`);
    }

    const orderVkBaseUrl = import.meta.env.VITE_ORDER_VK_URL || (() => {
      const hostname = window.location.hostname;
      if (hostname.includes('zrok.ultracafe.ru')) {
        return 'https://ordervk.zrok.ultracafe.ru';
      } else if (hostname.includes('ultracafe.ru')) {
        return 'https://order-vk.ultracafe.ru';
      }
      return 'http://localhost:3041';
    })();

    setPreviewUrl(`${orderVkBaseUrl}/#${hashParts.join('&')}`);
    setPreviewModalOpen(true);
  };

  const fetchApprovalData = useCallback(async () => {
    try {
      const result = await getApprovalData(token);
      
      if (result.success) {
        setData(result);
        const initialDecisions = new Map();
        result.posts.forEach((post) => {
          if (post.current_decision !== 'pending') {
            initialDecisions.set(post.id, {
              decision: post.current_decision,
              comment: post.comment || '',
            });
          }
        });
        setDecisions(initialDecisions);
      } else {
        const errorMessages = {
          token_expired: 'Срок действия ссылки истёк. Обратитесь к менеджеру за новой ссылкой.',
          token_not_found: 'Ссылка не найдена или недействительна.',
          invalid_token: 'Некорректная ссылка.',
          no_rounds: 'Воронка ещё не была отправлена на согласование.',
        };
        setError(errorMessages[result.error] || result.error || 'Ошибка загрузки данных');
      }
    } catch (err) {
      console.error('Error fetching approval data:', err);
      setError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchApprovalData();
    }
  }, [token, fetchApprovalData]);

  useEffect(() => {
    if (!submitted || !submitResult?.posts_scheduled) return;
    
    setPolling(true);
    
    const poll = async () => {
      try {
        const status = await getScheduleStatus(token);
        setScheduleStatus(status);
        
        if (status.schedule_status === 'completed' || status.schedule_status === 'failed') {
          setPolling(false);
          return;
        }
      } catch (err) {
        console.error('Ошибка получения статуса планирования:', err);
      }
    };
    
    poll();
    const interval = setInterval(poll, 2000);
    
    return () => {
      clearInterval(interval);
      setPolling(false);
    };
  }, [submitted, submitResult, token]);

  const handleDecision = (postId, decision) => {
    setDecisions((prev) => {
      const newDecisions = new Map(prev);
      const existing = newDecisions.get(postId);
      newDecisions.set(postId, {
        decision,
        comment: existing?.comment || '',
      });
      return newDecisions;
    });
  };

  const handleCommentChange = (postId, comment) => {
    setDecisions((prev) => {
      const newDecisions = new Map(prev);
      const existing = newDecisions.get(postId);
      if (existing) {
        newDecisions.set(postId, { ...existing, comment });
      }
      return newDecisions;
    });
  };

  const handleApproveAll = () => {
    if (!data) return;
    
    setDecisions((prev) => {
      const newDecisions = new Map(prev);
      data.posts.forEach((post) => {
        newDecisions.set(post.id, {
          decision: 'approved',
          comment: '',
        });
      });
      return newDecisions;
    });
  };

  const handleSubmit = async () => {
    const decisionsArray = [];
    
    decisions.forEach((value, postId) => {
      if (value.decision === 'approved' || value.decision === 'rejected') {
        decisionsArray.push({
          post_id: postId,
          decision: value.decision,
          comment: value.comment || undefined,
        });
      }
    });

    if (decisionsArray.length === 0) {
      setError('Необходимо принять решение хотя бы по одному посту');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const result = await submitApprovalDecisions(token, decisionsArray);

      if (result.success) {
        setSubmitResult({
          campaign_status: result.campaign_status,
          approved_count: result.approved_count,
          rejected_count: result.rejected_count,
          posts_scheduled: result.posts_scheduled,
        });
        setSubmitted(true);
      } else {
        setError(result.error || 'Ошибка отправки решений');
      }
    } catch (err) {
      console.error('Error submitting decisions:', err);
      setError('Ошибка соединения при отправке');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Загрузка...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-red-900/30 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-red-400 mb-2">Ошибка</h1>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const isRejected = submitResult?.campaign_status === 'smm_rejected';
    const approvedCount = submitResult?.approved_count || 0;
    const rejectedCount = submitResult?.rejected_count || 0;
    const totalCount = approvedCount + rejectedCount;

    const getScheduleDisplay = () => {
      if (!scheduleStatus) return null;
      
      const { schedule_status, schedule_status_display, schedule_progress, schedule_total, schedule_error } = scheduleStatus;
      
      if (schedule_status === 'completed') {
        return {
          icon: '🎉',
          color: 'text-green-400',
          text: `Посты запланированы в VK (${schedule_progress} из ${schedule_total})`,
        };
      }
      
      if (schedule_status === 'failed') {
        return {
          icon: '⚠️',
          color: 'text-yellow-400',
          text: 'Ошибка планирования постов',
          error: schedule_error,
        };
      }
      
      if (schedule_status === 'in_progress' || schedule_status === 'pending') {
        return {
          icon: '⏳',
          color: 'text-blue-400',
          text: schedule_status_display || 'Планирование постов в VK...',
          spinner: true,
        };
      }
      
      return null;
    };
    
    const scheduleDisplay = getScheduleDisplay();

    if (isRejected) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
          <div className="bg-orange-900/20 border border-orange-500/30 rounded-2xl p-8 max-w-lg text-center">
            <div className="text-4xl mb-4">📝</div>
            <h1 className="text-xl font-bold text-orange-400 mb-3">Воронка отправлена на доработку</h1>
            
            <div className="bg-white/5 rounded-lg p-4 mb-5 text-left">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-green-400">✓</span>
                  <span className="text-gray-300 text-sm">Одобрено: <span className="font-medium text-green-400">{approvedCount}</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-red-400">✕</span>
                  <span className="text-gray-300 text-sm">Отклонено: <span className="font-medium text-red-400">{rejectedCount}</span></span>
                </div>
                <span className="text-gray-500 text-sm">из {totalCount}</span>
              </div>
            </div>

            <div className="text-left space-y-3 mb-6">
              <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Что будет дальше</h2>
              <div className="flex gap-3 items-start">
                <span className="text-blue-400 mt-0.5 shrink-0">1.</span>
                <p className="text-gray-300 text-sm">Менеджер получит уведомление с вашими комментариями к отклонённым постам</p>
              </div>
              <div className="flex gap-3 items-start">
                <span className="text-blue-400 mt-0.5 shrink-0">2.</span>
                <p className="text-gray-300 text-sm">Отклонённые посты будут доработаны с учётом ваших замечаний</p>
              </div>
              <div className="flex gap-3 items-start">
                <span className="text-blue-400 mt-0.5 shrink-0">3.</span>
                <p className="text-gray-300 text-sm">Вам придёт новая ссылка для повторного согласования обновлённой воронки</p>
              </div>
            </div>

            {data?.employees && data.employees.length > 0 && (
              <div className="bg-white/5 rounded-lg p-4 text-left">
                <p className="text-gray-400 text-xs mb-2">Если есть вопросы, свяжитесь с менеджером:</p>
                {data.employees.map((emp, idx) => (
                  <div key={idx} className="text-sm">
                    <span className="text-white">{emp.name}</span>
                    {emp.phone && (
                      <a href={`tel:${emp.phone}`} className="text-blue-400 ml-2 hover:text-blue-300">{emp.phone}</a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-green-900/30 border border-green-500/30 rounded-2xl p-8 max-w-md text-center">
          <div className="text-4xl mb-4">✅</div>
          <h1 className="text-xl font-bold text-green-400 mb-2">Воронка согласована!</h1>
          <p className="text-gray-300 mb-2">
            {totalCount === 1
              ? 'Пост одобрен и будет опубликован в VK.'
              : `Все ${totalCount} постов одобрены и будут опубликованы в VK.`}
          </p>
          <p className="text-gray-400 text-sm mb-4">
            Менеджер уведомлён о результатах согласования.
          </p>
          
          {scheduleDisplay && (
            <div className="bg-white/10 rounded-lg p-4 mb-4">
              <div className={`flex items-center justify-center gap-2 ${scheduleDisplay.color}`}>
                {scheduleDisplay.spinner && (
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span className="text-lg">{scheduleDisplay.icon}</span>
                <span>{scheduleDisplay.text}</span>
              </div>
              {scheduleDisplay.error && (
                <p className="text-red-300 text-sm mt-2">
                  {scheduleDisplay.error}
                </p>
              )}
            </div>
          )}
          
          {polling && !scheduleDisplay && (
            <div className="flex items-center justify-center gap-2 text-blue-300 mb-4">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Планирование постов в VK...</span>
            </div>
          )}

          {data?.campaign?.channel_url && !polling && (
            <a
              href={data.campaign.channel_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.576-1.496c.588-.19 1.341 1.26 2.14 1.818.605.422 1.064.33 1.064.33l2.137-.03s1.117-.071.587-.964c-.043-.073-.308-.661-1.588-1.87-1.34-1.264-1.16-1.059.453-3.246.983-1.332 1.376-2.145 1.253-2.493-.117-.332-.84-.244-.84-.244l-2.406.015s-.178-.025-.31.056c-.13.079-.212.262-.212.262s-.382 1.03-.89 1.907c-1.07 1.85-1.499 1.948-1.674 1.832-.407-.267-.305-1.075-.305-1.648 0-1.793.267-2.54-.521-2.733-.262-.065-.454-.107-1.123-.114-.858-.009-1.585.003-1.996.208-.274.136-.485.44-.356.457.159.022.519.099.71.363.246.341.237 1.107.237 1.107s.142 2.11-.33 2.371c-.325.18-.77-.187-1.725-1.865-.489-.859-.859-1.81-.859-1.81s-.07-.176-.198-.272c-.154-.115-.37-.151-.37-.151l-2.286.015s-.343.01-.469.162c-.112.135-.009.414-.009.414s1.781 4.232 3.8 6.36c1.851 1.952 3.951 1.821 3.951 1.821h.953z"/>
              </svg>
              Перейти в группу ВК
            </a>
          )}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { campaign, posts, employees, expires_at } = data;
  const allPostsDecided = posts.length > 0 && posts.every((post) => {
    const decision = decisions.get(post.id);
    return decision && (decision.decision === 'approved' || decision.decision === 'rejected');
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {data.token_status && data.token_status !== 'pending' && (
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-2xl p-4 mb-6">
            <p className="text-blue-200 text-sm">
              Вы уже отправляли решения по этой воронке. Вы можете изменить их и отправить снова.
            </p>
          </div>
        )}

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              {campaign.channel_avatar_url && (
                <img
                  src={campaign.channel_avatar_url}
                  alt={campaign.channel_name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-white">{campaign.name}</h1>
                <p className="text-gray-400">
                  {campaign.brand_name} • {campaign.channel_name}
                  {data.round_number && data.round_number > 1 && (
                    <span className="ml-2 text-blue-400 text-sm">
                      (раунд #{data.round_number})
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-gray-400 text-sm">Срок действия ссылки:</p>
              <p className="text-yellow-400">{formatDate(expires_at)}</p>
            </div>
          </div>
        </div>

        {/* TODO: восстановить блок контактов, когда будет нужно
        {employees.length > 0 && (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-white/20">
            <h2 className="text-lg font-bold text-white mb-4">👥 Контакты для связи</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employees.map((emp, idx) => (
                <div key={idx} className="bg-white/5 rounded-lg p-4">
                  <div className="font-medium text-white">{emp.name}</div>
                  <div className="text-gray-400 text-sm">{emp.role_display}</div>
                  <div className="text-blue-400 text-sm">{emp.phone}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        */}

        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">
              📝 Посты для согласования ({posts.length})
            </h2>
            <button
              onClick={handleApproveAll}
              className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Одобрить все
            </button>
          </div>
          
          <div className="space-y-6">
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                channelName={campaign.channel_name}
                channelAvatarUrl={campaign.channel_avatar_url}
                brandTimezone={campaign.brand_timezone}
                decision={decisions.get(post.id)}
                onApprove={() => handleDecision(post.id, 'approved')}
                onReject={() => handleDecision(post.id, 'rejected')}
                onCommentChange={(comment) => handleCommentChange(post.id, comment)}
                onPreviewClick={(buttonType, productUid, postId) => openPreview(buttonType, productUid, postId)}
              />
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-4 mb-6 text-red-300">
            {error}
          </div>
        )}

        <div className="sticky bottom-6 bg-gray-900/90 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex justify-between items-center">
            <div className="text-gray-400 text-sm">
              Принято решений: {decisions.size} из {posts.length}
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting || !allPostsDecided}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 disabled:cursor-not-allowed text-white py-3 px-8 rounded-lg font-medium transition-colors"
            >
              {submitting ? 'Сохранение...' : 'Сохранить решения'}
            </button>
          </div>
          {!allPostsDecided && (
            <p className="text-yellow-400 text-sm mt-2 text-right">
              Необходимо принять решение по всем постам
            </p>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewModalOpen(false)}
        >
          <div
            className="bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-white/10">
              <h3 className="text-white font-medium">Форма для клиента</h3>
              <button
                onClick={() => setPreviewModalOpen(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="p-4 h-[70vh]">
              <iframe
                src={previewUrl}
                className="w-full h-full rounded-lg bg-white"
                title="Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
